// Voice provider — mounts once at the app root, owns the worker URL config
// and the Firebase ID token getter that the voice core uses to authenticate
// against the Cloudflare worker. Surfaces consume the provider through
// `useVoiceSession`.

import React, { createContext, useContext, useMemo, type ReactNode } from "react";
import { JUNGLE_CLOUDFLARE_BASE_URL } from "../../constants/GlobalTypesAndEnumsAndConstants";
import type { VoiceCoreConfig } from "../core";
import { getSignedInFirebaseUser } from "../../backend/utils/anonymous-read-session";

const VoiceCoreConfigContext = createContext<VoiceCoreConfig | null>(null);

interface VoiceProviderProps {
  children: ReactNode;
  // Override the default worker URL — useful for testing or for surfaces
  // that want to point at a staging worker. Defaults to JUNGLE_CLOUDFLARE_BASE_URL.
  workerBaseUrl?: string;
}

export function VoiceProvider({ children, workerBaseUrl }: VoiceProviderProps) {
  const coreConfig = useMemo<VoiceCoreConfig>(
    () => ({
      workerBaseUrl: workerBaseUrl ?? JUNGLE_CLOUDFLARE_BASE_URL,
      getFirebaseIdToken: async () => {
        const currentUser = getSignedInFirebaseUser();
        if (!currentUser) {
          return null;
        }
        try {
          // forceRefresh=false — Firebase auto-refreshes when the token is
          // within 5 minutes of expiry. Forcing refresh on every voice
          // session start would add ~200ms of latency for no benefit.
          return await currentUser.getIdToken(false);
        } catch {
          return null;
        }
      },
    }),
    [workerBaseUrl],
  );

  return (
    <VoiceCoreConfigContext.Provider value={coreConfig}>
      {children}
    </VoiceCoreConfigContext.Provider>
  );
}

/**
 * Internal — used by `useVoiceSession`. Throws if `VoiceProvider` isn't
 * mounted, so the misconfiguration is loud rather than silent.
 */
export function useVoiceCoreConfig(): VoiceCoreConfig {
  const config = useContext(VoiceCoreConfigContext);
  if (!config) {
    throw new Error(
      "useVoiceSession / useVoiceCoreConfig was called outside <VoiceProvider>. " +
        "Mount <VoiceProvider> at the app root.",
    );
  }
  return config;
}
